/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ClientRow } from '../../context/ClientManagementContext';

/**
 * The edit form's sections, as the drawer names them.
 *
 * `personal` and `contacts` are always expanded; the rest are collapsed
 * until something opens them.
 */
export type ClientFormSection =
  | 'personal' | 'contacts' | 'identity' | 'personalExtra'
  | 'address' | 'financial' | 'preferences';

/** One thing a complete registration is expected to carry. */
interface QualityField {
  id: string;
  /** Resolved against the `features/clients` namespace. */
  labelKey: string;
  /** Where the edit form keeps it, so the card can open onto it. */
  section: ClientFormSection;
  filled: (client: ClientRow) => boolean;
}

/**
 * What a registration needs before it can carry a contract.
 *
 * Computed here rather than read off the server: the `/scores` endpoint
 * that used to answer this was removed with churn and conversion, and
 * every field below already arrives on the client row. Keeping the list
 * beside the screen also means it states exactly which fields the edit
 * drawer will open onto, which a server-side percentage never did.
 *
 * Date of birth is deliberately absent. The client row carries it and the
 * form schema declares it, but no section actually renders an input for
 * it — listing it would give the broker a "Preencher" button onto a form
 * with nothing to fill.
 */
const QUALITY_FIELDS: QualityField[] = [
  { id: 'phone', labelKey: 'profile.overview.phone', section: 'contacts', filled: (c) => Boolean(c.phone) },
  { id: 'email', labelKey: 'profile.overview.email', section: 'contacts', filled: (c) => Boolean(c.email) },
  { id: 'document', labelKey: 'profile.overview.document', section: 'identity', filled: (c) => Boolean(c.cpf || c.cnpj || c.document) },
  { id: 'nationality', labelKey: 'drawer.personalExtra.nationality', section: 'personalExtra', filled: (c) => Boolean(c.nationality) },
  { id: 'profession', labelKey: 'drawer.personalExtra.profession', section: 'personalExtra', filled: (c) => Boolean(c.profession) },
  { id: 'maritalStatus', labelKey: 'drawer.personalExtra.maritalStatus', section: 'personalExtra', filled: (c) => Boolean(c.marital_status) },
  { id: 'rg', labelKey: 'drawer.identityExtra.rg', section: 'identity', filled: (c) => Boolean(c.rg) },
  { id: 'issuingAgency', labelKey: 'drawer.identityExtra.issuingAgency', section: 'identity', filled: (c) => Boolean(c.issuing_agency) },
  { id: 'zipCode', labelKey: 'drawer.address.zipCode', section: 'address', filled: (c) => Boolean(c.address_fields?.zip_code) },
  { id: 'street', labelKey: 'drawer.address.street', section: 'address', filled: (c) => Boolean(c.address_fields?.street || c.address) },
  { id: 'number', labelKey: 'drawer.address.number', section: 'address', filled: (c) => Boolean(c.address_fields?.number) },
  { id: 'neighborhood', labelKey: 'drawer.address.neighborhood', section: 'address', filled: (c) => Boolean(c.address_fields?.neighborhood) },
  { id: 'city', labelKey: 'drawer.address.city', section: 'address', filled: (c) => Boolean(c.address_fields?.city) },
  { id: 'monthlyIncome', labelKey: 'drawer.financial.monthlyIncome', section: 'financial', filled: (c) => c.monthly_income != null },
  { id: 'incomeProof', labelKey: 'drawer.financial.incomeProof', section: 'financial', filled: (c) => Boolean(c.income_proof) },
];


export interface ClientQuality {
  /** Whole percent of the expected fields that are filled. */
  percent: number;
  /** The unfilled fields, in the order a reader works down the form. */
  missing: Array<{ id: string; labelKey: string; section: ClientFormSection }>;
  total: number;
}

/**
 * How complete a client registration is, and what is still missing.
 *
 * @param client - The client row to measure.
 * @returns The percentage, the missing fields, and how many were expected.
 */
export function clientQuality(client: ClientRow): ClientQuality {
  const missing = QUALITY_FIELDS.filter((field) => !field.filled(client));
  const filled = QUALITY_FIELDS.length - missing.length;
  return {
    percent: Math.round((filled / QUALITY_FIELDS.length) * 100),
    missing: missing.map(({ id, labelKey, section }) => ({ id, labelKey, section })),
    total: QUALITY_FIELDS.length,
  };
}
